<template>
  <div
    class="portal-page relative min-h-screen w-full flex flex-col overflow-hidden bg-gradient-to-b from-[#F0F7FC] via-[#FFFFFF] to-[#F1F5F9]"
  >
    <!-- 背景装饰光斑 -->
    <div
      class="pointer-events-none absolute -top-24 -left-24 w-72 h-72 md:w-96 md:h-96 rounded-full bg-teal-200/30 blur-3xl"
      aria-hidden="true"
    />
    <div
      class="pointer-events-none absolute -top-16 -right-16 w-64 h-64 md:w-80 md:h-80 rounded-full bg-orange-200/25 blur-3xl"
      aria-hidden="true"
    />
    <div
      class="pointer-events-none absolute top-1/3 right-0 w-48 h-48 rounded-full bg-sky-100/40 blur-2xl"
      aria-hidden="true"
    />

    <div class="relative z-10 flex flex-col flex-1 w-full">
      <!-- Header / Hero -->
      <header
        class="portal-header bg-transparent"
        :style="{ paddingTop: 'env(safe-area-inset-top)' }"
      >
        <div class="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
          <!-- Logo -->
          <div class="flex items-center gap-3 mb-10 md:mb-12">
            <img :src="brandLogo" alt="安心陪护" class="portal-brand-logo" />
            <span class="text-lg font-bold text-slate-900 tracking-tight">安心陪护</span>
            <span class="px-2.5 py-0.5 rounded-md text-xs font-medium text-caregiver-green bg-teal-50 border border-teal-100">
              一期 · v0.1
            </span>
          </div>

          <!-- Hero 标题 -->
          <h1 class="text-3xl md:text-[2.5rem] font-bold text-slate-900 tracking-tight leading-tight">
            医院专业陪护
          </h1>
          <p class="mt-3 text-xl md:text-[1.75rem] font-bold text-family-blue leading-snug">
            人工调度 · 全程透明 · 一对一交付
          </p>
          <p class="mt-5 text-sm md:text-base text-slate-500 leading-relaxed max-w-3xl">
            连接住院家属、KYC 持证护工与运营调度三端。极简一期，先把「找护工 → 派单 → 履约 → 结算」完整跑通。
          </p>

          <!-- 特性标签 -->
          <div class="mt-6 flex flex-wrap gap-3">
            <span
              v-for="tag in featureTags"
              :key="tag.label"
              class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/80 border border-slate-100 text-xs md:text-sm text-slate-600 shadow-[0_2px_10px_rgb(0,0,0,0.04)] backdrop-blur-sm"
            >
              <component :is="'span'" v-html="tag.icon" class="flex-shrink-0" />
              {{ tag.label }}
            </span>
          </div>
        </div>
      </header>

      <!-- 三端入口卡片 -->
      <main class="flex-1 w-full max-w-6xl mx-auto px-5 md:px-8 pb-8 md:pb-10">
        <section class="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl">
          <article
            v-for="entry in portalEntries"
            :key="entry.role"
            class="portal-card group bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-slate-100/60 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(0,0,0,0.09)] active:scale-[0.98]"
          >
            <div class="p-6 md:p-8 flex flex-col">
              <div
                class="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-200 group-hover:scale-105"
                :style="{ backgroundColor: entry.iconBg }"
              >
                <span v-html="entry.iconSvg" class="flex items-center justify-center" />
              </div>

              <h2 class="text-base md:text-lg font-semibold text-slate-800 mb-2.5">{{ entry.title }}</h2>
              <p class="text-sm text-slate-500 leading-relaxed flex-1">{{ entry.description }}</p>
              <p class="mt-5 text-xs text-slate-400">{{ entry.platform }}</p>

              <button
                type="button"
                class="mt-6 w-full py-3 px-4 rounded-xl text-white text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 active:scale-95 shadow-[0_4px_14px_rgb(0,0,0,0.12)] hover:shadow-[0_6px_20px_rgb(0,0,0,0.16)] flex items-center justify-center gap-1.5"
                :style="{ backgroundColor: entry.color }"
                @click="handleEnter(entry)"
              >
                进入
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </article>
        </section>
      </main>

      <!-- 底部公告 -->
      <footer
        v-if="announcementVisible"
        class="w-full max-w-6xl mx-auto px-5 md:px-8 pb-6 md:pb-8"
        :style="{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }"
      >
        <div
          class="bg-red-50/90 border border-red-100 rounded-2xl px-5 py-4 md:py-5 flex items-start gap-3 shadow-[0_4px_20px_rgb(254,202,202,0.35)]"
          role="alert"
        >
          <span class="flex-shrink-0 mt-0.5 text-admin-coral">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
          </span>
          <p class="flex-1 text-xs md:text-sm text-slate-600 leading-relaxed">
            {{ announcement }}
          </p>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import brandLogo from "../../common/brandLogo";

const ICONS = {
  checkBlue:
    '<svg class="w-4 h-4 text-family-blue" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>',
  cameraTeal:
    '<svg class="w-4 h-4 text-caregiver-green" viewBox="0 0 24 24" fill="currentColor"><path d="M12 15.2a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4z"/><path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/></svg>',
  headsetRed:
    '<svg class="w-4 h-4 text-admin-coral" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1a9 9 0 00-9 9v7c0 1.66 1.34 3 3 3h1v-8H5v-2a7 7 0 0114 0v2h-2v8h1c1.66 0 3-1.34 3-3v-7a9 9 0 00-9-9z"/></svg>',
  familyHeart:
    '<svg class="w-7 h-7 text-family-blue" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>',
  caregiverId:
    '<svg class="w-7 h-7 text-caregiver-green" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>',
  monitorRed:
    '<svg class="w-7 h-7 text-admin-coral" viewBox="0 0 24 24" fill="currentColor"><path d="M21 2H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7v2H8v2h8v-2h-2v-2h7c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H3V4h18v12z"/></svg>',
};

const ROLE_FLOW = {
  family: {
    role: "family",
    label: "家属/患者端",
    flow: ["登录/注册", "选择场景与医院", "挑选护工", "提交预约订单", "跟踪订单与评价"],
    nextRoute: "/family/login",
  },
  caregiver: {
    role: "caregiver",
    label: "护工端",
    flow: ["资质上传(KYC)", "开启接单开关", "确认派单", "现场打卡与护理日志", "申请完工与提现"],
    nextRoute: "/caregiver",
  },
  admin: {
    role: "admin",
    label: "运营管理端",
    flow: ["登录后台", "KYC资质审核", "人工指派派单", "监控履约与代操作", "财务对账与标记打款"],
    nextRoute: "/login",
  },
};

export default {
  name: "PortalPage",
  data() {
    return {
      brandLogo,
      announcementVisible: true,
      announcement:
        "家属端可体验下单与订单跟踪；PC 后台可体验人工调度指派、订单池监控与护工管理。",
      featureTags: [
        { label: "健康证 + 职业证", icon: ICONS.checkBlue },
        { label: "实拍打卡", icon: ICONS.cameraTeal },
        { label: "调度员 7x24 跟进", icon: ICONS.headsetRed },
      ],
      caregiverEntryVisible: false,
      entries: [
        {
          role: "family",
          title: "我是家属 / 患者",
          platform: "手机 H5 · 用户端",
          color: "#0082D4",
          iconBg: "rgba(0, 130, 212, 0.1)",
          iconSvg: ICONS.familyHeart,
          description: "在线提交陪护需求，由调度团队人工指派专业护工",
        },
        {
          role: "caregiver",
          title: "我是护工",
          platform: "手机 H5 · 护工端",
          color: "#00A685",
          iconBg: "rgba(0, 166, 133, 0.1)",
          iconSvg: ICONS.caregiverId,
          description: "接单、打卡、记录护理日志，月底申请提现",
        },
        {
          role: "admin",
          title: "我是运营管理员",
          platform: "PC 管理后台",
          color: "#E0625B",
          iconBg: "rgba(224, 98, 91, 0.1)",
          iconSvg: ICONS.monitorRed,
          description: "KYC 审核、人工指派、监控履约、财务对账",
        },
      ],
    };
  },
  computed: {
    portalEntries() {
      if (this.caregiverEntryVisible) {
        return this.entries;
      }
      return this.entries.filter((item) => item.role !== "caregiver");
    },
  },
  methods: {
    handleEnter(entry) {
      const config = ROLE_FLOW[entry.role];
      console.group(`[安心陪护] 角色入口 · ${config.label}`);
      console.log("角色标识:", config.role);
      console.log("流转路径:", config.flow.join(" → "));
      console.table(
        config.flow.map((step, index) => ({
          步骤: index + 1,
          环节: step,
          状态: index === 0 ? "待进入" : "未开始",
        }))
      );
      if (config.nextRoute) {
        console.log("即将跳转:", config.nextRoute);
        console.groupEnd();
        this.$router.push(config.nextRoute);
      } else {
        console.log("提示: 该端 H5 页面一期开发中，敬请期待");
        console.groupEnd();
      }
    },
  },
  mounted() {
    document.title = "安心陪护";
    document.documentElement.classList.add("portal-route");
    document.body.classList.add("portal-route");
  },
  beforeDestroy() {
    document.documentElement.classList.remove("portal-route");
    document.body.classList.remove("portal-route");
  },
};
</script>

<style scoped>
.portal-page {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", "Helvetica Neue", Arial, sans-serif;
  background-size: 100% 100%;
}

.portal-brand-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  flex-shrink: 0;
}
</style>

<style>
html.portal-route,
body.portal-route {
  min-height: 100%;
  height: auto;
  width: 100%;
  background: transparent;
}

body.portal-route #app {
  min-height: 100%;
  height: auto;
  width: 100%;
  background: transparent;
}
</style>
